import { useState, useEffect } from 'react'
import { Plus, Search, FileText, Clock, CheckCircle, XCircle, Receipt, Banknote } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import useInvoiceStore from '../../stores/useInvoiceStore'
import useReceiptStore from '../../stores/useReceiptStore'

/**
 * Invoices & Receipts Page - WITH TABS
 * Tab 1: Invoices (الفواتير)
 * Tab 2: Receipts (سندات القبض)
 */
const Invoices = () => {
  const navigate = useNavigate()
  const [mainTab, setMainTab] = useState('invoices') // 'invoices' or 'receipts'
  const [invoiceStatusTab, setInvoiceStatusTab] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Get invoices from Zustand store (connected to Backend API)
  const { invoices, loading: invoicesLoading, error: invoicesError, fetchInvoices } = useInvoiceStore()

  // Get receipts from Zustand store
  const { receipts, loading: receiptsLoading, error: receiptsError, fetchReceipts } = useReceiptStore()

  // Load data from API on mount
  useEffect(() => {
    fetchInvoices()
    fetchReceipts()
  }, [])

  // Filter invoices based on status tab
  const filteredInvoices = invoices.filter((invoice) => {
    if (invoiceStatusTab !== 'all' && invoice.status !== invoiceStatusTab) return false
    const customerName = invoice.customer?.name || invoice.customerName || ''
    const invoiceNum = invoice.invoice_number || invoice.invoiceNumber || ''
    if (searchQuery && !customerName.includes(searchQuery) && !invoiceNum.includes(searchQuery)) return false
    return true
  })

  // Filter receipts based on search
  const filteredReceipts = receipts.filter((receipt) => {
    const customerName = receipt.customer?.name || ''
    const receiptNum = receipt.receipt_number || ''
    if (searchQuery && !customerName.includes(searchQuery) && !receiptNum.includes(searchQuery)) return false
    return true
  })

  const invoiceStatusTabs = [
    { id: 'all', label: 'الكل', count: invoices.length },
    { id: 'cash', label: 'نقدي', count: invoices.filter(i => i.status === 'cash').length },
    { id: 'credit', label: 'آجل', count: invoices.filter(i => i.status === 'credit').length },
    { id: 'paid', label: 'مدفوعة', count: invoices.filter(i => i.status === 'paid').length },
  ]

  const loading = mainTab === 'invoices' ? invoicesLoading : receiptsLoading
  const error = mainTab === 'invoices' ? invoicesError : receiptsError

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {mainTab === 'invoices' ? 'الفواتير' : 'سندات القبض'}
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/receipts/create')}
              className="btn btn-secondary flex items-center gap-2"
              title="إنشاء سند قبض مستقل"
            >
              <Receipt className="w-5 h-5" />
              <span className="hidden sm:inline">سند قبض</span>
            </button>
            <button
              onClick={() => navigate('/invoices/create')}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              <span>فاتورة</span>
            </button>
          </div>
        </div>

        {/* Main Tabs (Invoices / Receipts) */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMainTab('invoices')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              mainTab === 'invoices'
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <FileText className="w-5 h-5" />
            الفواتير ({invoices.length})
          </button>
          <button
            onClick={() => setMainTab('receipts')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
              mainTab === 'receipts'
                ? 'bg-primary-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Receipt className="w-5 h-5" />
            سندات القبض ({receipts.length})
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={mainTab === 'invoices' ? 'ابحث عن فاتورة أو عميل...' : 'ابحث عن سند قبض أو عميل...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pr-10"
          />
        </div>

        {/* Invoice Status Tabs (only show when on invoices tab) */}
        {mainTab === 'invoices' && (
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {invoiceStatusTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setInvoiceStatusTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  invoiceStatusTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="p-4 pb-24">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">
              {mainTab === 'invoices' ? 'جاري تحميل الفواتير...' : 'جاري تحميل سندات القبض...'}
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              خطأ في التحميل
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => mainTab === 'invoices' ? fetchInvoices() : fetchReceipts()}
              className="btn btn-primary"
            >
              إعادة المحاولة
            </button>
          </div>
        ) : mainTab === 'invoices' ? (
          // INVOICES TAB CONTENT
          filteredInvoices.length > 0 ? (
            <div className="space-y-3">
              {filteredInvoices.map((invoice) => (
                <InvoiceCard key={invoice.id} invoice={invoice} onClick={() => navigate(`/invoices/${invoice.id}`)} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                لا توجد فواتير
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchQuery ? 'لا توجد نتائج للبحث' : 'ابدأ بإنشاء فاتورة جديدة'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate('/invoices/create')}
                  className="btn btn-primary"
                >
                  <Plus className="w-5 h-5 ml-2" />
                  إنشاء فاتورة جديدة
                </button>
              )}
            </div>
          )
        ) : (
          // RECEIPTS TAB CONTENT
          filteredReceipts.length > 0 ? (
            <div className="space-y-3">
              {filteredReceipts.map((receipt) => (
                <ReceiptCard key={receipt.id} receipt={receipt} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                لا توجد سندات قبض
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {searchQuery ? 'لا توجد نتائج للبحث' : 'ابدأ بإنشاء سند قبض جديد'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate('/receipts/create')}
                  className="btn btn-primary"
                >
                  <Plus className="w-5 h-5 ml-2" />
                  إنشاء سند قبض
                </button>
              )}
            </div>
          )
        )}
      </main>
    </div>
  )
}

/**
 * Invoice Card Component
 */
const InvoiceCard = ({ invoice, onClick }) => {
  const getStatusConfig = (status) => {
    const configs = {
      paid: {
        icon: CheckCircle,
        class: 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
        label: 'مدفوعة',
        badgeClass: 'badge badge-success',
      },
      pending: {
        icon: Clock,
        class: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
        label: 'معلقة',
        badgeClass: 'badge badge-warning',
      },
      credit: {
        icon: Clock,
        class: 'text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-900/30',
        label: 'آجل',
        badgeClass: 'badge badge-warning',
      },
      cash: {
        icon: CheckCircle,
        class: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
        label: 'نقدي',
        badgeClass: 'badge badge-info',
      },
      cancelled: {
        icon: XCircle,
        class: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
        label: 'ملغاة',
        badgeClass: 'badge badge-danger',
      },
    }
    return configs[status] || configs.pending
  }

  const statusConfig = getStatusConfig(invoice.status)
  const StatusIcon = statusConfig.icon

  // Map API fields to component fields
  const customerName = invoice.customer?.name || invoice.customerName || 'غير محدد'
  const invoiceNumber = invoice.invoice_number || invoice.invoiceNumber || ''
  const amount = invoice.total || invoice.amount || 0
  const date = invoice.invoice_date || invoice.date || ''

  return (
    <div
      onClick={onClick}
      className="card p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${statusConfig.class}`}>
            <StatusIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {customerName}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {invoiceNumber}
            </p>
          </div>
        </div>
        <span className={statusConfig.badgeClass}>{statusConfig.label}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <p>{date}</p>
        </div>
        <div className="text-left">
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {parseFloat(amount).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Receipt Card Component
 */
const ReceiptCard = ({ receipt }) => {
  // Map API fields
  const customerName = receipt.customer?.name || 'غير محدد'
  const receiptNumber = receipt.receipt_number || ''
  const amount = receipt.amount || 0
  const date = receipt.receipt_date || receipt.created_at || ''
  const paymentMethod = receipt.payment_method?.name || receipt.payment_method?.name_e || 'نقدي'
  const referenceNumber = receipt.reference_number || null

  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
            <Receipt className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {customerName}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {receiptNumber}
            </p>
          </div>
        </div>
        <div className="text-left">
          <p className="text-xl font-bold text-green-600 dark:text-green-400">
            {parseFloat(amount).toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <div className="text-gray-600 dark:text-gray-400">
            <p>{date}</p>
          </div>
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <Banknote className="w-4 h-4" />
            <span>{paymentMethod}</span>
          </div>
        </div>
        {referenceNumber && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            رقم المرجع: {referenceNumber}
          </div>
        )}
      </div>

      {receipt.notes && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            📝 {receipt.notes}
          </p>
        </div>
      )}
    </div>
  )
}

export default Invoices
